import { sendGTMEvent } from '@next/third-parties/google';
import type Hls from 'hls.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { EWebEvents } from '@/components/VideoPlayer/events';

const HOVER_DELAY_MS = 250;
const PREVIEW_DURATION_MS = 8000;

interface IPreviewItem {
  id: string;
  title: string;
  categories?: string | string[];
  source?: string;
  isAuthenticated?: boolean;
}

export function useVideoPreview(
  videoUrl: string,
  enabled: boolean,
  item?: IPreviewItem,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRestartRef = useRef<() => void>(() => {});
  const previewStartTimeRef = useRef<number | null>(null);

  // Keep latest values in refs so callbacks never need them as deps
  const videoUrlRef = useRef(videoUrl);
  videoUrlRef.current = videoUrl;
  const itemRef = useRef(item);
  itemRef.current = item;

  const [isHovering, setIsHovering] = useState(false);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (previewStartTimeRef.current !== null) {
      const secondsWatched = Math.round(
        (Date.now() - previewStartTimeRef.current) / 1000,
      );
      sendGTMEvent({
        event: EWebEvents.PREVIEW_END,
        event_category: 'video_preview',
        video_id: itemRef.current?.id ?? '',
        video_title: itemRef.current?.title ?? '',
        video_url: videoUrlRef.current,
        categories: itemRef.current?.categories ?? '',
        preview_source: itemRef.current?.source ?? 'unknown',
        is_authenticated: itemRef.current?.isAuthenticated ?? false,
        seconds_watched: secondsWatched,
      });
      previewStartTimeRef.current = null;
    }
    setIsHovering(false);
  }, []);

  scheduleRestartRef.current = () => {
    previewTimerRef.current = setTimeout(() => {
      const video = videoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      scheduleRestartRef.current();
    }, PREVIEW_DURATION_MS);
  };

  const startPreview = useCallback(async () => {
    const url = videoUrlRef.current;
    if (!url || !videoRef.current) return;
    const video = videoRef.current;

    const { default: HlsClass } = await import('hls.js');

    if (HlsClass.isSupported()) {
      if (!hlsRef.current) {
        hlsRef.current = new HlsClass({
          maxBufferLength: 5,
          maxMaxBufferLength: 8,
          backBufferLength: 0,
          startLevel: 0,
          lowLatencyMode: false,
          fragLoadingMaxRetry: 1,
          manifestLoadingMaxRetry: 1,
          progressive: true,
          autoStartLoad: true,
          abrEwmaDefaultEstimate: 300000,
        });
        hlsRef.current.loadSource(url);
        hlsRef.current.attachMedia(video);
        hlsRef.current.on(HlsClass.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => {});
          previewStartTimeRef.current = Date.now();
          sendGTMEvent({
            event: EWebEvents.PREVIEW_START,
            event_category: 'video_preview',
            video_id: itemRef.current?.id ?? '',
            video_title: itemRef.current?.title ?? '',
            video_url: url,
            categories: itemRef.current?.categories ?? '',
            preview_source: itemRef.current?.source ?? 'unknown',
            is_authenticated: itemRef.current?.isAuthenticated ?? false,
          });
          scheduleRestartRef.current();
        });
      } else {
        video.play().catch(() => {});
      }
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      video.load();
      video.play().catch(() => {});
      previewStartTimeRef.current = Date.now();
      sendGTMEvent({
        event: EWebEvents.PREVIEW_START,
        event_category: 'video_preview',
        video_id: itemRef.current?.id ?? '',
        video_title: itemRef.current?.title ?? '',
        video_url: url,
        categories: itemRef.current?.categories ?? '',
      });
      scheduleRestartRef.current();
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!enabled || !videoUrlRef.current) return;
    setIsHovering(true);
    timerRef.current = setTimeout(startPreview, HOVER_DELAY_MS);
  }, [enabled, startPreview]);

  const handleMouseLeave = useCallback(() => {
    cleanup();
  }, [cleanup]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { videoRef, handleMouseEnter, handleMouseLeave, isHovering };
}
